
  import { computed, defineComponent, onMounted, ref, watch } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';
  import { reinitializeComponents } from '@/core/plugins/keenthemes';
  import Lightgallery from 'lightgallery/vue';
  import lgZoom from 'lightgallery/plugins/zoom';
  import lgVideo from 'lightgallery/plugins/video';
  import Multiselect from '@vueform/multiselect';

  let lightGallery;
  let lightGalleryDoc;

  export default defineComponent({
    name: 'ads-creating',
    components: {
      ErrorMessage,
      Field,
      Form,
      Lightgallery,
      Multiselect,
    },
    data() {
      return {
        thumbnailImages: [],
        plugins: [lgZoom, lgVideo],
      };
    },
    methods: {
      previewImages(e) {
        const files = Array.from(e.target.files);
        this.thumbnailImages = [];

        const compressAndAddImage = async (file) => {
          try {
            const compressedImage = await compressImage(file, 800, 800, 0.8);
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            const imageURL = URL.createObjectURL(compressedImage);
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            this.thumbnailImages.push(imageURL);
          } catch (error) {
            console.error('Error compressing image:', error);
          }
        };
        const compressImage = (file, maxWidth, maxHeight, quality) => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = function (event) {
              const image = new Image();
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              //@ts-ignore
              image.src = event.target.result;
              image.onload = function () {
                let canvas = document.createElement('canvas');
                let width = image.width;
                let height = image.height;
                if (width > height) {
                  if (width > maxWidth) {
                    height *= maxWidth / width;
                    width = maxWidth;
                  }
                } else {
                  if (height > maxHeight) {
                    width *= maxHeight / height;
                    height = maxHeight;
                  }
                }
                canvas.width = width;
                canvas.height = height;
                let ctx = canvas.getContext('2d');
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                //@ts-ignore
                ctx.drawImage(image, 0, 0, width, height);
                canvas.toBlob(
                  function (compressed) {
                    resolve(compressed);
                  },
                  'image/jpeg',
                  quality
                );
              };
              image.onerror = function (error) {
                reject(error);
              };
            };
            reader.onerror = function (error) {
              reject(error);
            };
            reader.readAsDataURL(file);
          });
        };

        files.slice(0, 10).forEach((file) => {
          compressAndAddImage(file);
        });
      },
      removeImage(index) {
        this.thumbnailImages.splice(index, 1);
      },
    },
    async setup() {
      const { t, te } = useI18n();
      const { can } = useAbility();
      const store = useStore();
      const router = useRouter();
      const submitButton = ref<HTMLButtonElement | null>(null);
      const submitVideoButton = ref<HTMLButtonElement | null>(null);
      const submitImagesButton = ref<HTMLButtonElement | null>(null);
      const submitDocButton = ref<HTMLButtonElement | null>(null);
      const loading = ref(false);
      const createdAd = ref<any | null>(null);
      const selectedArea = ref<number>(0);
      const selectedAdTypesId = ref<number>(0);
      const selectedPropertyCategoryId = ref<number>(0);
      const selectedPropertyTypesId = ref<number>(0);
      const selectedUser = ref<number>(0);
      const videoInput = ref('');
      const docInput = ref('');
      const guideVideo = ref('');
      const adsPdf = ref('');
      const imagesInput = ref('');
      const certifiedCheckRequest = ref(false);
      const loanRequest = ref(false);
      const appUsers = ref<any>([]);
      const propertyTypesCategories = ref<any>([]);
      const propertyTypes = ref<any>([]);
      const price = ref<number>(0);
      const priceVal = ref<number>(0);
      const adDetails = ref<string | null>(null);
      const size = ref<number>(0);
      const numRooms = ref<number>(0);
      const numBathrooms = ref<number>(0);
      const thumbnail = ref('/media/avatars/upload.png');
      const showCatAndType = ref<boolean>(true);
      const isHighQuality = ref(false);
      const isSpecial = ref(false);

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      const adsSchema = Yup.object().shape({
        adTypesId: Yup.number()
          .notOneOf([0], () => translate('AD_TYPE__IS_REQUIRED_FIELD'))
          .required(() => translate('AD_TYPE_IS_REQUIRED_FIELD'))
          .label('Ad type'),
        size: Yup.number()
          .required(() => translate('SIZE_IS_REQUIRED_FIELD'))
          .label('size'),
        propertyCategoryId: Yup.number().when('adTypesId', (adTypesId, field) =>
          adTypesId !== 5 && adTypesId > 0
            ? field
                .notOneOf([0], 'PROPERTY_CATEGORY_IS_REQUIRED_FIELD')
                .required('PROPERTY_CATEGORY_IS_REQUIRED_FIELD')
                .label('propertyCategoryId')
            : field
        ),
        propertyTypeId: Yup.number().when('adTypesId', (adTypesId, field) =>
          adTypesId !== 5 && adTypesId > 0
            ? field
                .notOneOf([0], () =>
                  translate('PROPERTY_TYPE_IS_REQUIRED_FIELD')
                )
                .required('PROPERTY_TYPE_IS_REQUIRED_FIELD')
                .label('propertyTypeId')
            : field
        ),
      });

      const onSubmitCreate = async (values) => {
        await store.dispatch(Actions.ADD_BODY_CLASSNAME, 'page-loading-over');
        if (submitButton.value) {
          // eslint-disable-next-line
          submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        const payload = {
          adTypeId: Number(selectedAdTypesId.value),
          isHighQuality: isHighQuality.value,
          isSpecial: isSpecial.value,
          price:
            Number(priceVal.value) == 0
              ? Number(price.value)
              : Number(priceVal.value),
          additionalInfo: adDetails.value ? adDetails.value : null,
          size: Number(size.value),
          userId: Number(selectedUser.value),
          areaId: Number(selectedArea.value),
          propertyTypeId: Number(selectedPropertyTypesId.value)
            ? Number(selectedPropertyTypesId.value)
            : undefined,
          propertyCategoryId: Number(selectedPropertyCategoryId.value)
            ? Number(selectedPropertyCategoryId.value)
            : undefined,
          numRooms: Number(values.numRooms)
            ? Number(values.numRooms)
            : undefined,
          numBathrooms: Number(values.numBathrooms)
            ? Number(values.numBathrooms)
            : undefined,
        };
        // if (Number(selectedPropertyTypesId.value))
        //   payload['propertyTypeId'] = Number(selectedPropertyTypesId.value);

        // if (Number(selectedPropertyCategoryId.value))
        //   payload['propertyCategoryId'] = Number(
        //     selectedPropertyCategoryId.value
        //   );

        // if (numRooms.value) payload['numRooms'] = Number(numRooms.value);
        // if (numBathrooms.value)
        //   payload['numBathrooms'] = Number(numBathrooms.value);

        const result = await store.dispatch(Actions.CREATE_AD, payload);
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          await store.dispatch(
            Actions.REMOVE_BODY_CLASSNAME,
            'page-loading-over'
          );
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          submitButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
          submitButton.value!.disabled = false;
        } else {
          await store.dispatch(
            Actions.REMOVE_BODY_CLASSNAME,
            'page-loading-over'
          );
          //Deactivate indicator
          submitButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
          submitButton.value!.disabled = false;
          createdAd.value = result;
          Swal.fire({
            text: translate('SUCCESSFULLY_CREATED_AD'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          });
        }
      };
      const onInit = (detail) => {
        lightGallery = detail.instance;
      };
      const updateSlides = () => {
        lightGallery.refresh();
      };
      const onInitDoc = (detail) => {
        lightGalleryDoc = detail.instance;
      };
      const updateSlidesDoc = () => {
        lightGalleryDoc.refresh();
      };
      const previewVideo = (e) => {
        videoInput.value = e.target.files[0];
        const file = e.target.files[0];
        if (file) {
          guideVideo.value = URL.createObjectURL(file);
          updateSlides();
        }
      };
      const previewDoc = (e) => {
        docInput.value = e.target.files[0];
        const file = e.target.files[0];
        if (file) {
          adsPdf.value = URL.createObjectURL(file);
          updateSlides();
        }
      };

      const previewImage = (e) => {
        const file = e.target.files[0];
        thumbnail.value = URL.createObjectURL(file);
      };

      const onSubmitImages = async () => {
        if (submitImagesButton.value) {
          // eslint-disable-next-line
          submitImagesButton.value!.disabled = true;
          submitImagesButton.value.setAttribute('data-kt-indicator', 'on');
        }

        if (!imagesInput.value) {
          return;
        }
        await store.dispatch(Actions.ADD_BODY_CLASSNAME, 'page-loading-over');

        const videoFormData = new FormData();
        // eslint-disable-next-line
        for (let i = 0; i < imagesInput.value.length; i++) {
          videoFormData.append('images', imagesInput.value[i]);
        }

        await store.dispatch(Actions.UPLOAD_ADS_IMAGES, {
          data: videoFormData,
          adId: createdAd.value?.id,
        });
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          await store.dispatch(
            Actions.REMOVE_BODY_CLASSNAME,
            'page-loading-over'
          );
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          submitImagesButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
          submitImagesButton.value!.disabled = false;
        } else {
          await store.dispatch(
            Actions.REMOVE_BODY_CLASSNAME,
            'page-loading-over'
          );
          Swal.fire({
            text: translate('SUCCESSFULLY_UPLOADED_AD_IMAGES'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            //Deactivate indicator
            submitImagesButton.value?.removeAttribute('data-kt-indicator');
          });
        }
      };

      const onSubmitVideo = async () => {
        if (submitVideoButton.value) {
          // eslint-disable-next-line
          submitVideoButton.value!.disabled = true;
          submitVideoButton.value.setAttribute('data-kt-indicator', 'on');
        }

        if (!videoInput.value) {
          return;
        }
        await store.dispatch(Actions.ADD_BODY_CLASSNAME, 'page-loading-over');

        const videoFormData = new FormData();
        // eslint-disable-next-line
        videoFormData.append('video', videoInput.value);

        await store.dispatch(Actions.UPLOAD_ADS_VIDEO, {
          data: videoFormData,
          adId: createdAd.value?.id,
        });
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          await store.dispatch(
            Actions.REMOVE_BODY_CLASSNAME,
            'page-loading-over'
          );
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          submitVideoButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
          submitVideoButton.value!.disabled = false;
        } else {
          await store.dispatch(
            Actions.REMOVE_BODY_CLASSNAME,
            'page-loading-over'
          );
          Swal.fire({
            text: translate('SUCCESSFULLY_UPLOADED_AD_VIDEO'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            //Deactivate indicator
            submitVideoButton.value?.removeAttribute('data-kt-indicator');
          });
        }
      };

      const onSubmitDoc = async () => {
        if (submitDocButton.value) {
          // eslint-disable-next-line
          submitDocButton.value!.disabled = true;
          submitDocButton.value.setAttribute('data-kt-indicator', 'on');
        }

        if (!docInput.value) {
          return;
        }
        await store.dispatch(Actions.ADD_BODY_CLASSNAME, 'page-loading-over');

        const documentFormData = new FormData();
        // eslint-disable-next-line
        documentFormData.append('document', docInput.value);

        await store.dispatch(Actions.UPLOAD_ADS_DOCUMENT, {
          data: documentFormData,
          adId: createdAd.value?.id,
        });
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          await store.dispatch(
            Actions.REMOVE_BODY_CLASSNAME,
            'page-loading-over'
          );

          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          submitDocButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
          submitDocButton.value!.disabled = false;
        } else {
          await store.dispatch(
            Actions.REMOVE_BODY_CLASSNAME,
            'page-loading-over'
          );

          Swal.fire({
            text: translate('SUCCESSFULLY_UPLOADED_AD_DOC'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            //Deactivate indicator
            submitDocButton.value?.removeAttribute('data-kt-indicator');
          });
        }
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('adsCreating'), [translate('ads')]);
        reinitializeComponents();
      });

      watch(
        () => selectedAdTypesId.value,
        async () => {
          await store.dispatch(Actions.ADD_BODY_CLASSNAME, 'page-loading-over');
          if (selectedAdTypesId.value) {
            await store.dispatch(
              Actions.GET_ALL_PROPERTY_CATEGORIES_BY_AD_TYPE_ID,
              { adType: selectedAdTypesId.value }
            );
            propertyTypesCategories.value =
              store.getters.propertyAllCategoryList;
            selectedPropertyTypesId.value = 0;
            selectedPropertyCategoryId.value = 0;
            if (
              Number(selectedAdTypesId.value) != 3 ||
              Number(selectedAdTypesId.value) == 4
            ) {
              numBathrooms.value = 0;
              numRooms.value = 0;
            }
            if (propertyTypesCategories.value.length <= 0) {
              propertyTypesCategories.value = [];
              propertyTypes.value = [];
              showCatAndType.value = false;
              await store.dispatch(
                Actions.REMOVE_BODY_CLASSNAME,
                'page-loading-over'
              );
            }
            await store.dispatch(
              Actions.REMOVE_BODY_CLASSNAME,
              'page-loading-over'
            );
          }
        }
      );

      watch(
        () => selectedPropertyCategoryId.value,
        async () => {
          await store.dispatch(Actions.ADD_BODY_CLASSNAME, 'page-loading-over');
          if (selectedPropertyCategoryId.value) {
            await store.dispatch(Actions.GET_ALL_PROPERTY_TYPE, {
              type: 'ads',
              propertyTypesCategoryId: selectedPropertyCategoryId.value,
            });
            propertyTypes.value = store.getters.propertyTypesAllList;
            selectedPropertyTypesId.value = 0;

            await store.dispatch(
              Actions.REMOVE_BODY_CLASSNAME,
              'page-loading-over'
            );
          }
        }
      );

      watch(
        () => selectedUser.value,
        async () => {
          if (selectedUser.value) {
            const userObj = appUsers.value.value.filter(
              (user) => user.id == selectedUser.value
            )[0];
          }
        }
      );

      watch(videoInput, (newVideoInput, oldVideoInput) => {
        if (!newVideoInput) {
          videoInput.value = oldVideoInput;
        }
      });

      await store.dispatch(Actions.GET_ALL_AREAS_LIST, { type: 'ads' });

      await store.dispatch(Actions.GET_ALL_AD_TYPES);
      await store.dispatch(Actions.GET_SERVICE_LIST);
      await store.dispatch(Actions.GET_ALL_APP_USERS);
      appUsers.value = computed(() => store.getters.usersAllList);

      await store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        submitVideoButton,
        onSubmitVideo,
        onSubmitImages,
        onSubmitCreate,
        submitButton,
        translate,
        adsSchema,
        goBack,
        can,
        loading,
        createdAd,
        appUsers,
        adTypes: computed(() => store.getters.adTypesAllList),
        areas: computed(() => store.getters.allListAreas),
        locations: computed(() => store.getters.organizerLocationsList),
        adsPackages: computed(() => store.getters.adsServicesDdl),
        propertyTypesCategories,
        propertyTypes,
        selectedArea,
        selectedAdTypesId,
        selectedPropertyCategoryId,
        selectedPropertyTypesId,
        selectedUser,
        videoInput,
        docInput,
        guideVideo,
        adsPdf,
        imagesInput,
        previewVideo,
        previewDoc,
        onInit,
        updateSlides,
        onInitDoc,
        updateSlidesDoc,
        certifiedCheckRequest,
        loanRequest,
        price,
        priceVal,
        adDetails,
        size,
        numRooms,
        numBathrooms,
        previewImage,
        thumbnail,
        onSubmitDoc,
        isHighQuality,
        isSpecial,
      };
    },
  });
